<template>
	<v-menu v-model="menu" :close-on-content-click="false" location="bottom end" origin="top end" offset="4" max-width="310">
		<template #activator="{ props: menuProps }">
			<v-tooltip v-if="tooltip && tooltipSummary" location="top">
				<template #activator="{ props: tooltipProps }">
					<div v-bind="{ ...menuProps, ...tooltipProps }">
						<v-text-field
							class="planning-input-dense"
							append-inner-icon="mdi-menu-down"
							density="compact"
							variant="outlined"
							:placeholder="summary ? '' : label"
							readonly
							hide-details
						>
							<template #prepend-inner>
								<span v-if="summary" class="mr-2">{{ $t('menu_groups') }}</span>
								<v-chip v-if="summary" class="indicator-chip" variant="flat">{{ summary }}</v-chip>
							</template>
						</v-text-field>
					</div>
				</template>

				<span style="white-space: pre-line">
					{{ tooltipSummary }}
				</span>
			</v-tooltip>

			<v-text-field
				v-else
				v-bind="menuProps"
				class="planning-input-dense"
				append-inner-icon="mdi-menu-down"
				density="compact"
				variant="outlined"
				:placeholder="summary ? '' : label"
				readonly
				hide-details
			>
				<template #prepend-inner>
					<span v-if="summary" class="mr-2">{{ $t('menu_groups') }}</span>
					<v-chip v-if="summary" class="indicator-chip" variant="flat">{{ summary }}</v-chip>
				</template>
			</v-text-field>
		</template>

		<v-card>
			<v-list>
				<v-text-field
					v-model="search"
					density="compact"
					variant="outlined"
					placeholder="Search"
					append-inner-icon="mdi-magnify"
					hide-details
					class="mb-3"
				/>

				<div v-for="item in filteredItems" :key="item.value" class="py-2 item-wrapper">
					<div class="selectable-row" @click="toggle(item)">
						<div>
							<v-icon class="mr-3" size="18" :color="isSelected(item) ? 'primary-green' : 'grey'">
								{{ isSelected(item) ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
							</v-icon>
							<span class="mt-1">{{ item.label }}</span>
						</div>

						<v-icon size="18" color="grey">mdi-account-group</v-icon>
					</div>

					<div v-if="isSelected(item) && item.children" class="children-container pl-8 mt-1">
						<div v-for="child in item.children" :key="child.value" class="child-item py-1">
							<span class="text-caption text-grey-darken-1">{{ child.label }}</span>
						</div>
					</div>
				</div>
			</v-list>

			<v-divider class="my-2" />

			<v-card-actions class="d-flex justify-end px-4 pb-2">
				<v-btn variant="text" size="small" @click="reset">
					{{ $t('adapter_manage_reset') }}
				</v-btn>
				<v-btn color="primary-green" size="small" @click="apply">
					{{ $t('action_apply') }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { OptionItem, FilterValue } from '../../types/components/types';
import { usePlanningStore } from '../../store';

const props = defineProps<{
	label?: string;
	options: OptionItem[];
	modelValue: FilterValue;
	tooltip: boolean;
}>();

const emit = defineEmits<{
	'update:modelValue': [value: FilterValue];
	apply: [value: FilterValue];
}>();

// Safe clone function avoiding structuredClone proxy issues
const cloneSelected = (selected: OptionItem[] = []): OptionItem[] => {
	return selected ? selected.map((item) => ({ ...item })) : [];
};

const localValue = ref<FilterValue>({
	selected: cloneSelected(props.modelValue?.selected),
});

const menu = ref<boolean>(false);
const planningStore = usePlanningStore();
const search = ref('');

// Sync local state when modelValue changes from outside
watch(
	() => props.modelValue,
	(val) => {
		localValue.value = {
			selected: cloneSelected(val?.selected),
		};
	},
	{ deep: true }
);

// Revert unapplied local selection changes when menu is closed without clicking apply
watch(menu, (isOpen) => {
	if (!isOpen) {
		localValue.value = {
			selected: cloneSelected(props.modelValue?.selected),
		};
		search.value = '';
	}
});

const filteredItems = computed(() => {
	if (!search.value) {
		return props.options;
	}
	return props.options.filter((o) => o.label.toLowerCase().includes(search.value.toLowerCase()));
});

const tooltipSummary = computed(() => {
	const labels = (props.modelValue?.selected || []).map((item) => item.label);
	return labels.join('\n');
});

const toggle = (item: OptionItem) => {
	const idx = localValue.value.selected.findIndex((s) => s.value === item.value);

	if (idx === -1) {
		localValue.value.selected.push({ ...item });
	} else {
		localValue.value.selected.splice(idx, 1);
	}
};

const isSelected = (item: OptionItem) => localValue.value.selected.some((s) => s.value === item.value);

// Summary chip count reads only from the COMMITTED props.modelValue
const summary = computed(() => {
	const count = props.modelValue?.selected?.length || 0;
	return count ? count : '';
});

const reset = () => {
	localValue.value.selected = [];
	apply();
};

const apply = () => {
	const finalSelected = cloneSelected(localValue.value.selected);
	emit('update:modelValue', { selected: finalSelected });
	emit('apply', { selected: finalSelected });
	menu.value = false;
};
</script>

<style scoped>
.planning-input-dense :deep(.v-field__input) {
	min-height: 32px;
	font-size: 0.75rem;
}

.item-wrapper {
	display: block;
}

.selectable-row {
	cursor: pointer;
	transition: none;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.selectable-row:hover {
	background-color: transparent !important;
	background: transparent !important;
}

/* Scrollable Fixed Height Container for Child Items */
.children-container {
	display: flex;
	flex-direction: column;
	max-height: 120px;
	overflow-y: auto;
	border-left: 1px dashed #e0e0e0;
	padding-left: 12px !important;
	margin-left: 8px;
}

.children-container::-webkit-scrollbar {
	width: 4px;
}
.children-container::-webkit-scrollbar-thumb {
	background: #cccccc;
	border-radius: 4px;
}

.child-item {
	font-size: 0.85rem;
}

:deep(.v-overlay__content) {
	max-width: calc(100vw - 32px);
}

.v-card {
	width: 310px;
	max-width: calc(100vw - 32px);
}

.v-list {
	border-radius: 8px;
	width: 310px;
	max-width: calc(100vw - 32px);
	min-height: 400px;
	height: 400px;
	padding: 8px;
	padding-top: 14px;
	overflow-y: auto;
}

:deep .v-list-item__prepend > .v-badge .v-icon,
.v-list-item__prepend > .v-icon,
.v-list-item__append > .v-badge .v-icon,
.v-list-item__append > .v-icon {
	opacity: 1;
}

.indicator-chip {
	width: 25px;
	height: 25px;
	background-color: #10565c;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
}

.v-toolbar {
	z-index: 1000;
}
</style>
